import { Conversation } from "../../api";

export function isLastSevenDaysRange(dateToCheck: Date) {
  // Get the current date
  const currentDate = new Date();
  // Calculate the date 2 days ago
  const twoDaysAgo = new Date();
  twoDaysAgo.setDate(currentDate.getDate() - 2);
  // Calculate the date 8 days ago
  const eightDaysAgo = new Date();
  eightDaysAgo.setDate(currentDate.getDate() - 8);
  // Ensure the comparison dates are in the correct order
  // We need eightDaysAgo to be earlier than twoDaysAgo
  return dateToCheck >= eightDaysAgo && dateToCheck <= twoDaysAgo;
}

export const segregateItems = (items: Conversation[]) => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  // Sort items by updatedAt in descending order (most recent first)
  const sortedItems = [...items].sort(
    (a, b) =>
      new Date(b.updatedAt ? b.updatedAt : new Date()).getTime() -
      new Date(a.updatedAt ? a.updatedAt : new Date()).getTime()
  );

  const groupedItems: {
    Today: Conversation[];
    Yesterday: Conversation[];
    Last7Days: Conversation[];
    Older: Conversation[];
    Past: { [key: string]: Conversation[] };
  } = {
    Today: [],
    Yesterday: [],
    Last7Days: [],
    Older: [],
    Past: {},
  };

  sortedItems.forEach((item) => {
    const itemDate = new Date(item.updatedAt ? item.updatedAt : new Date());
    const itemDateOnly = itemDate.toDateString();
    if (itemDateOnly === today.toDateString()) {
      groupedItems.Today.push(item);
    } else if (itemDateOnly === yesterday.toDateString()) {
      groupedItems.Yesterday.push(item);
    } else if (isLastSevenDaysRange(itemDate)) {
      groupedItems.Last7Days.push(item);
    } else {
      groupedItems.Older.push(item);
    }
  });

  // Sort each group by updatedAt in descending order to ensure proper order
  const sortByUpdatedAt = (conversations: Conversation[]) =>
    conversations.sort(
      (a, b) =>
        new Date(b.updatedAt ? b.updatedAt : new Date()).getTime() -
        new Date(a.updatedAt ? a.updatedAt : new Date()).getTime()
    );

  const finalResult = [
    { title: `Today`, entries: sortByUpdatedAt(groupedItems.Today) },
    {
      title: `Yesterday`,
      entries: sortByUpdatedAt(groupedItems.Yesterday),
    },
    {
      title: `Last 7 days`,
      entries: sortByUpdatedAt(groupedItems.Last7Days),
    },
    {
      title: `Older`,
      entries: sortByUpdatedAt(groupedItems.Older),
    },
  ];

  return finalResult;
};

const SELECTED_MODEL_COOKIE_KEY = "selectedAiModel";
const RESPONSE_MODE_COOKIE_KEY = "responseMode";
const SHOWED_STARTUP_SCREEN_COOKIE_KEY = "showedStartupScreen"
const MODEL_TEMPERATURE_PREFIX = "temperature_"
const DEFAULT_MODEL_TEMPERATURE = 0.0;
const PREFERRED_SEARCH_INDEX_COOKIE_KEY = "preferredSearchIndex";

export const setPreferredAiModel = (value: string): void => {  
  setCookie(SELECTED_MODEL_COOKIE_KEY, value, 365);
}

export const getPreferredAiModel = (): string | null => {  
  return getCookie(SELECTED_MODEL_COOKIE_KEY);
}

export const setPreferredAiTemperature = (modelName: string, value: number): void => {  
  setCookie(MODEL_TEMPERATURE_PREFIX + modelName, value.toString(), 365);
}

export const getPreferredAiTemperature = (modelName: string | undefined): number | null => {  

  if (modelName === undefined) {
    return DEFAULT_MODEL_TEMPERATURE;
  }

  const value = getCookie(MODEL_TEMPERATURE_PREFIX + modelName);

  if (value == null) {
    return DEFAULT_MODEL_TEMPERATURE;
  }

  return Number.parseFloat(value);
}

export const setPreferredResponseMode = (value: string): void => {  
  setCookie(RESPONSE_MODE_COOKIE_KEY, value, 365);
}

export const getPreferredResponseMode = (): string | null => {  
  return getCookie(RESPONSE_MODE_COOKIE_KEY);
}

export const setShowedStartupScreen = (value: boolean): void => {  
  setCookie(SHOWED_STARTUP_SCREEN_COOKIE_KEY, value.toString(), 365);
}

export const getShowedStartupScreen = (): boolean => {  
  const value = getCookie(SHOWED_STARTUP_SCREEN_COOKIE_KEY);

  if (value == null) {
    return false;
  }

  const result = JSON.parse(value.toLowerCase());

  if (result == null) {
    return false;
  }

  return result;
}

export const setPreferredSearchIndex = (value: string): void => {
  setCookie(PREFERRED_SEARCH_INDEX_COOKIE_KEY, value, 365);
}

export const getPreferredSearchIndex = (): string | null => {
  return getCookie(PREFERRED_SEARCH_INDEX_COOKIE_KEY);
}

// Cookie utility functions
export const setCookie = (name: string, value: string, days: number = 30): void => {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
};

export const getCookie = (name: string): string | null => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

